import Konva from 'konva';
import { v4 as uuidv4 } from 'uuid';

import HaspObject from './HaspObject';
import { processIconText } from '../utils/iconFonts';
import { resolvePadding, onPaddingStyle } from './properties/v2/padding.js';

// options	    string	""	    List of items separated by \n
// val	        int16	0	    The number of the selected item
// text	        string	""	    Read-only The text of the selected item
// direction	byte	0	    Direction where the dropdown expands:
//                              0 = down, 1 = up, 2 = left, 3 = right
//                              Note: up and down are superseeded by the screen size.
//show_selected bool	true	Show the selected option or a static text
// max_height	int16	3/4 of screen height	The maximum height of the open drop-down list

export default class HaspDropdown extends HaspObject {

    // listOptions = ["Option1", "Option2", "Option3"];
    itemHeight = 40;
    max_height = 200;

    constructor(config) {
        super(config);
        this.rect.opacity(0);
        this.loadOptions(config.options);
        this.load(config);

        this.drawOptions();

        this.on('transform', (e) => {
            this.drawOptions();
        });
        this.on('transformend', (e) => { this.drawOptions(); });
        this.on('preview-expanded', (e) => {
            this.drawOptions();
            this.getLayer()?.updateTransformer();
        });
        this.on('dblclick dbltap', (e) => {
            e.cancelBubble = true;
            this.previewExpanded = !this.previewExpanded;
            this.adjust('preview-expanded');
        });
        this.onStyle('main', 'radius', () => { this.textrect.cornerRadius(this.style('main').radius); })
        // pdrect/selectRect only exist while previewExpanded — guard accordingly.
        this.onStyle('item_buttons', 'radius', () => { this.pdrect?.cornerRadius(this.style('item_buttons').radius); })
        this.onStyle('main', 'bg_color', () => { this.textrect.fill(this.style('main').bg_color); })
        this.onStyle('selected', 'bg_color', () => { this.selectRect?.fill(this.style('selected').bg_color); })
        this.onStyle('main', 'text_font', () => { this.drawOptions(); })
        this.onStyle('main', 'text_color', () => { this.drawOptions(); })
        this.onStyle('item_buttons', 'text_color', () => { this.drawOptions(); })
        // Pre-existing gap inherited from before this migration — item_buttons'
        // bg_color never had a reactive listener at all, so changing it only
        // ever took effect on the next full drawOptions() rebuild (e.g. resize).
        this.onStyle('item_buttons', 'bg_color', () => { this.pdrect?.fill(this.style('item_buttons').bg_color); })
        this.onStyle('main', 'border_width', () => { this.textrect.strokeWidth(this.style('main').border_width); })
        this.onStyle('item_buttons', 'border_width', () => { this.pdrect?.strokeWidth(this.style('item_buttons').border_width); })
        this.onStyle('main', 'border_color', () => { this.textrect.stroke(this.style('main').border_color); })
        this.onStyle('item_buttons', 'border_color', () => { this.pdrect?.stroke(this.style('item_buttons').border_color); })
        // Shorthand + all four per-side overrides, one subscription helper.
        onPaddingStyle(this, 'main', () => { this.drawOptions(); })
        this.onStyle('item_buttons', 'padding', () => { this.drawOptions(); })

        this.on('update', (e) => { this.drawOptions(); })
    }

    load(config) {
        if (config.max_height) {
            this.max_height = config.max_height;
            this.height(this.itemHeight + this.max_height)
        }
    }

    /**
     * Iterate the config options and for every \n char
     * we create a new item
     * @param {*} options 
     */
    loadOptions(options) {
        if (options) {
            this.listOptions = [];
            const opt_list = options.split('\n')
            opt_list.forEach(option => {
                if (option !== '\n') { //new row for every \n char
                    this.listOptions.push(option)
                }
            });
        }
    }

    redraw(args) {
        this.drawOptions();
    }

    setNewSize(size) {
        super.setNewSize(size);
        this.drawOptions();
    }

    /**
     * Collapsed preview shows only the fixed-height header row, so vertical
     * handles have nothing meaningful to resize — only width is adjustable.
     * Expanded preview shows the full popup and can be resized freely.
     */
    getResizeMode() {
        return this.previewExpanded ? 'both' : 'horizontal';
    }

    drawOptions() {
        // Read each part's whole resolved style once — everything below
        // pulls from these, never from flat/suffixed properties directly.
        const main = this.style('main');
        const itemButtons = this.style('item_buttons');
        const selected = this.style('selected');

        if (this.labelGroup !== undefined)
            this.labelGroup.destroy()
        this.labelGroup = new Konva.Group({
            x: 0,
            y: 0,
            width: this.width(),
            height: this.height(),
            // opacity: 0.5
        })

        this.textrect = new Konva.Rect({
            id: uuidv4(),
            x: 0,
            y: 0,
            width: this.width(),
            // height: this.itemHeight,
            fill: main.bg_color,
            stroke: main.border_color,
            strokeWidth: (main.border_width ?? 0) * 2,
            // Was previously only ever applied reactively (see the 'radius'
            // listener below) and never set at construction — since this
            // rect is fully rebuilt on almost every redraw (resize, preview
            // toggle, any other style change), the corner radius silently
            // reset to Konva's default whenever a rebuild happened for an
            // unrelated reason. Must be set here too.
            cornerRadius: main.radius,
            draggable: false,
            obj: 'text_rect',
            selectable: false,
            listening: false,
        });
        this.labelGroup.add(this.textrect);

        const w = this.width();
        // Effective per-side padding insets the closed row's text (see v2/padding.js).
        const pad = resolvePadding(main);
        const label = new Konva.Text({
            id: uuidv4(),
            x: pad.left + 3,
            y: pad.top,
            width: w - pad.left - pad.right,
            // height: this.itemHeight,
            obj: 'optionlabel',
            verticalAlign: 'middle',
            text: processIconText(this.listOptions[0]),
            fontSize: main.text_font,
            fontFamily: 'Roboto Condensed',
            align: 'left',
            fill: main.text_color,
            listening: false,
            strokeWidth: 1,
        });
        this.labelGroup.add(label);

        this.itemHeight = label.height() + 18;
        this.textrect.height(this.itemHeight + pad.top + pad.bottom);
        label.height(this.itemHeight);

        // LVGL's real dropdown draws a small down/up chevron on the right of
        // the closed row that flips direction to reflect expanded state.
        const pd_butt = new Konva.Text({
            id: uuidv4(),
            x: this.width() - this.itemHeight,
            y: 0,
            width: this.itemHeight,
            height: this.itemHeight,
            obj: 'pd_butt',
            verticalAlign: 'middle',
            align: 'center',
            text: this.previewExpanded ? '▲' : '▼', // ▲ / ▼
            fontSize: Math.max(10, Math.round(this.itemHeight * 0.35)),
            fontFamily: 'Roboto Condensed',
            fill: main.text_color,
            listening: true,
        });
        pd_butt.on('click tap dblclick dbltap', (e) => {
            e.cancelBubble = true;
        });
        pd_butt.on('click tap', () => {
            this.previewExpanded = !this.previewExpanded;
            this.adjust('preview-expanded');
        });

        this.labelGroup.add(pd_butt);

        // max_height is the popup list height derived from the stored,
        // always-expanded geometry (this.height()) — it is never used to
        // adjust that geometry back. Real firmware exports this separately
        // from the closed row height (see objectExport below).
        this.max_height = Math.max(0, this.height() - this.itemHeight);

        // Cleared here since drawOptions() already destroyed the previous
        // labelGroup (and everything in it) above.
        this.pdrect = undefined;
        this.selectRect = undefined;

        if (this.previewExpanded) {
            this.pdrect = new Konva.Rect({
                id: uuidv4(),
                x: 0,
                y: this.textrect.height(),
                width: this.width(),
                height: this.max_height,
                fill: itemButtons.bg_color,
                stroke: itemButtons.border_color,
                strokeWidth: (itemButtons.border_width ?? 0) * 2,
                // Same construction-time fix as textrect above.
                cornerRadius: itemButtons.radius,
                draggable: false,
                obj: 'dropdown_rect',
                selectable: false,
                listening: false
            });
            this.labelGroup.add(this.pdrect);

            const nw = this.width();
            const numItems = Math.floor(this.max_height / this.itemHeight);
            for (var index = 0; index < numItems && index < this.listOptions.length; index++) {
                const option = this.listOptions[index];
                if (index === 0) {
                    this.selectRect = new Konva.Rect({
                        id: uuidv4(),
                        x: itemButtons.padding,
                        y: ((index) * this.itemHeight) + this.textrect.height(),
                        width: nw - itemButtons.padding,
                        height: this.itemHeight,
                        fill: selected.bg_color,
                        draggable: false,
                        obj: 'select_rect',
                        selectable: false,
                        listening: false
                    });
                    this.labelGroup.add(this.selectRect);
                }
                const itemLabel = new Konva.Text({
                    id: uuidv4(),
                    x: itemButtons.padding + 4,
                    y: ((index) * this.itemHeight) + this.textrect.height(),
                    width: nw - itemButtons.padding,
                    height: this.itemHeight,
                    obj: 'optionlabel',
                    verticalAlign: 'middle',
                    text: processIconText(option),
                    fontSize: main.text_font,
                    fontFamily: 'Roboto Condensed',
                    align: 'left',
                    fill: itemButtons.text_color,
                    listening: false,
                    strokeWidth: 1,
                    opacity: 0.5,
                });
                this.labelGroup.add(itemLabel);
            }
        }

        this.add(this.labelGroup);
        // Bounding box / resize-handle footprint (this.rect) tracks the CURRENT
        // preview state without ever touching this.height() — collapsed shows
        // just the header, expanded matches the full stored geometry.
        this.rect.height(this.previewExpanded ? this.height() : this.itemHeight);
        this.idTextObj.moveToTop();
    }

    objectExport(page, objectData) {
        //use /n to identify new line
        objectData.opacity = undefined;
        objectData.options = this.listOptions.join('\n')
        objectData.h = this.itemHeight;
        objectData.max_height = this.max_height;
    }

}
